import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { api } from '../../apiConfig';
import AddressDropdown from './AddressDropdown';
import FacilitiesChart from './FacilitiesChart';
import FacilityInfo from './FacilityInfo';
import { useFormContext } from './FormContext';
import { BusinessInfo, BusinessType, Facility } from './types';

interface Props {
  data: BusinessInfo;
  onChange: (field: string, value: any) => void;
  errors: { [key: string]: string };
  handleSetFullAddress: (fullAddress: {
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  }) => void;
}

const BusinessInformation = (props: Props) => {
  const baseBusinessInfo = {
    facilityName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    businessType: { id: 0, name: '' },
    isCorporate: false,
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      height: 54,
      minHeight: 54,
      width: 240,
      borderColor: props.errors?.businessType ? 'red' : provided.borderColor,
      boxShadow: props.errors?.businessType
        ? '0 0 0 .5px red'
        : provided.boxShadow,
      '&:hover': {
        borderColor: props.errors?.businessType ? 'red' : provided.borderColor,
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 500,
    }),
  };
  const { formData } = useFormContext();
  const [businessInfo, setBusinessInfo] = useState<
    BusinessInfo | Record<string, any>
  >(formData.businessInfo || baseBusinessInfo);
  const [facilityInfoForm, setFacilityInfoForm] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [facilityToEdit, setFacilityToEdit] = useState<Facility | null>(null);
  const [facilityIndex, setFacilityIndex] = useState<number | null>(null);

  const addFacility = () => {
    setFacilityInfoForm(true);
    setFacilityIndex(null);
    setFacilityToEdit(null);
  };

  const getBusinessTypes = async () => {
    var response = await api.get(`/api/customers/businesstypes`);
    setBusinessTypes(response.data);
  };

  useEffect(() => {
    getBusinessTypes();
  }, []);

  useEffect(() => {
    setBusinessInfo(formData.businessInfo || {});
  }, [formData]);

  return (
    <Box
      sx={{
        padding: '20px',
      }}
    >
      <Stack
        width={{ xs: '100%', sm: '90%', md: '80%', lg: '60%' }}
        maxWidth='800px'
        minWidth='200px'
      >
        <Stack position={'relative'}>
        <Typography style={{ color: '#808080', fontSize: 28 }}>
          Business Information
        </Typography>
        <Tooltip
          title='Please provide facility legal name and address'
          sx={{ position: 'absolute', top: 4, left: 280 }}
        >
          <IconButton size='small'>
            <InfoOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        </Stack>
        <Stack>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            spacing={2}
            marginTop={2}
          >
            <TextField
              label='Facility Name'
              name='facilityName'
              value={props.data.facilityName}
              onChange={(e) => {
                props.onChange('facilityName', e.target.value);
              }}
              fullWidth
              required
              margin='normal'
              sx={{ backgroundColor: '#fff' }}
              error={Boolean(props.errors?.businessFacilityName)}
              helperText={false}
            />
          </Stack>
          <AddressDropdown
            setAddress1={(value: any) => props.onChange('address', value)}
            setFullAddress={props.handleSetFullAddress}
            inputValue={props.data?.address || ''}
            data={props.data}
            errors={props.errors}
            errorKey='businessAddress'
            errorCityKey='businessCity'
            errorStateKey='businessState'
            errorZipKey='businessZip'
            onChange={props.onChange}
          />
        </Stack>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          spacing={2}
          marginTop={2}
        >
          <TextField
            label='Phone'
            name='phone'
            value={props.data.phone}
            onChange={(e) => props.onChange('phone', e.target.value)}
            fullWidth
            type='tel'
            sx={{ backgroundColor: '#fff', maxHeight: 54 }}
            error={Boolean(props.errors?.businessPhone)}
            helperText={false}
            required
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              input.value = input.value.replace(/[^0-9]/g, '');
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Select
              options={[
                ...businessTypes
                  .filter((type) => type.name.toLowerCase() !== 'other')
                  .map((type) => ({ value: type.id, label: type.name }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
                ...businessTypes
                  .filter((type) => type.name.toLowerCase() === 'other')
                  .map((type) => ({ value: type.id, label: type.name })),
              ]}
              placeholder='Business Type *'
              value={
                businessTypes.find(
                  (option) => option.id === props.data.businessType?.id
                )
                  ? {
                      value: props.data.businessType?.id,
                      label: props.data.businessType?.name,
                    }
                  : null
              }
              onChange={(selectedOption) => {
                if (selectedOption) {
                  props.onChange('businessType', {
                    id: selectedOption.value,
                    name: selectedOption.label,
                  });
                }
              }}
              styles={customStyles}
            />
            {businessInfo?.businessType?.name === 'Other' && (
              <TextField
                label='Other Business Type'
                name='otherBusinessType'
                value={props.data.otherBusinessType}
                onChange={(e) =>
                  props.onChange('otherBusinessType', e.target.value)
                }
                fullWidth
                margin='normal'
                sx={{ backgroundColor: '#fff', marginBottom: 3 }}
              />
            )}
          </div>
        </Stack>
        <FormControl style={{ marginTop: 32, position: 'relative' }}>
          <Tooltip
            title='Does your facility include more than one facility? If yes click add facility to add additional facilities including legal names and addresses'
            sx={{ position: 'absolute', left: 150, zIndex: 200, top:-5 }}
          >
            <IconButton size='small'>
              <InfoOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <FormLabel>Are you Corporate?</FormLabel>
          <RadioGroup
            row
            name='isCorporate'
            value={props.data.isCorporate ?? false}
            onChange={(e) =>
              props.onChange('isCorporate', e.target.value === 'true')
            }
          >
            <FormControlLabel value='true' control={<Radio />} label='Yes' />
            <FormControlLabel value='false' control={<Radio />} label='No' />
          </RadioGroup>
        </FormControl>
        {props.data.isCorporate && (
          <Stack justifyContent={'flex-end'} direction={'row'} marginTop={3}>
            <Button
              variant='contained'
              color='info'
              onClick={() => {
                addFacility();
                setFacilityInfoForm(true);
                window.scrollTo(0, 0);
              }}
            >
              <AddIcon sx={{ marginRight: 1 }} />
              Add Facility
            </Button>
          </Stack>
        )}
        {facilityInfoForm && (
          <FacilityInfo
            showForm={facilityInfoForm}
            onClose={() => {
              setFacilityInfoForm(false);
            }}
            facilityToEdit={facilityToEdit}
            setFacilityInfoForm={setFacilityInfoForm}
            facilityIndex={facilityIndex}
            setFacilityIndex={setFacilityIndex}
          />
        )}
      </Stack>
      {facilityInfoForm && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            minHeight: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 50,
            transition: 'opacity 0.3s ease',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
          onClick={() => setFacilityInfoForm(false)}
        />
      )}
      {formData.facilities?.length > 0 && (
        <>
          <Typography
            variant='h5'
            component='h2'
            sx={{ color: '#808080', marginTop: 10 }}
          >
            Facilities
          </Typography>
          <FacilitiesChart
            setOpenEditForm={setFacilityInfoForm}
            setFacilityToEdit={setFacilityToEdit}
            setFacilityIndex={setFacilityIndex}
          />
        </>
      )}
    </Box>
  );
};

export default BusinessInformation;
